/*
 * Sound + confetti effects. Every sound is synthesized with Web Audio,
 * so there are no audio files to ship.
 */

let ctx: AudioContext | null = null;

const audio = () => {
  ctx ??= new AudioContext();
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
};

const noise = (c: AudioContext, seconds: number) => {
  const buf = c.createBuffer(1, Math.ceil(c.sampleRate * seconds), c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  return src;
};

/** One printer-feed tick: two short band-passed noise clicks. */
export function tick() {
  const c = audio();
  const t = c.currentTime;
  const click = (freq: number, at: number, len: number) => {
    const src = noise(c, len);
    const bp = c.createBiquadFilter();
    const g = c.createGain();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(freq, at);
    bp.Q.setValueAtTime(5, at);
    g.gain.setValueAtTime(0.42, at);
    g.gain.exponentialRampToValueAtTime(0.001, at + len);
    src.connect(bp).connect(g).connect(c.destination);
    src.start(at);
  };
  click(2600, t, 0.012);
  click(900, t + 0.018, 0.018);
}

/** Digital glitch: a quick run of random square-wave blips. */
export function glitch() {
  const c = audio();
  const t = c.currentTime;
  for (let i = 0; i < 7; i++) {
    const at = t + i * 0.028;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "square";
    o.frequency.setValueAtTime(150 + Math.random() * 1800, at);
    g.gain.setValueAtTime(0.05, at);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.025);
    o.connect(g).connect(c.destination);
    o.start(at);
    o.stop(at + 0.03);
  }
}

/** Short rising arpeggio for a success moment. */
export function win() {
  const c = audio();
  const t = c.currentTime;
  [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
    const at = t + i * 0.08;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(f, at);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.2, at + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.25);
    o.connect(g).connect(c.destination);
    o.start(at);
    o.stop(at + 0.26);
  });
}

const reducedMotion = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Confetti "boom" from a screen point: squares and circles that flutter down and fade. */
export function confetti(x: number, y: number) {
  if (reducedMotion()) return;
  const dpr = devicePixelRatio || 1;
  const canvas = document.createElement("canvas");
  canvas.width = innerWidth * dpr;
  canvas.height = innerHeight * dpr;
  canvas.style.cssText = "position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:2000";
  document.body.appendChild(canvas);
  const g = canvas.getContext("2d")!;
  g.scale(dpr, dpr);

  const colors = ["#ff577f", "#ff884b", "#ffd384", "#fff9b0"];
  const parts = Array.from({ length: 30 }, () => {
    const a = Math.random() * Math.PI * 2;
    const v = 3 + Math.random() * 7;
    return {
      x,
      y,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v - 4,
      rot: Math.random() * 360,
      spin: (Math.random() - 0.5) * 12,
      flip: Math.random() * 360,
      size: 6 + Math.random() * 6,
      color: colors[Math.floor(Math.random() * colors.length)]!,
      circle: Math.random() < 0.5,
      life: 1,
    };
  });

  const frame = () => {
    g.clearRect(0, 0, innerWidth, innerHeight);
    let alive = false;
    for (const p of parts) {
      p.vx *= 0.96;
      p.vy = p.vy * 0.96 + 0.35;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.spin;
      p.flip += 8;
      p.life -= 0.012;
      if (p.life <= 0) continue;
      alive = true;
      const w = p.size * Math.cos((p.flip * Math.PI) / 180);
      g.save();
      g.globalAlpha = p.life;
      g.translate(p.x, p.y);
      g.rotate((p.rot * Math.PI) / 180);
      g.fillStyle = p.color;
      if (p.circle) {
        g.beginPath();
        g.ellipse(0, 0, Math.abs(w) / 2, p.size / 2, 0, 0, Math.PI * 2);
        g.fill();
      } else {
        g.fillRect(-w / 2, -p.size / 2, w, p.size);
      }
      g.restore();
    }
    if (alive) requestAnimationFrame(frame);
    else canvas.remove();
  };
  requestAnimationFrame(frame);
}
